// Hooks
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  startTransition,
} from "react";
import { useMedplum } from "@medplum/react";

// Services
import { buildUnreadNotificationTaskSearchParams } from "@benchmark/core/services";
import {
  NOTIFICATION_TASK_TYPE_SYSTEM,
  NOTIFICATION_TYPE_DEFINITIONS,
} from "@benchmark/core/constants";
import { reconcileServiceApprovalNotices } from "./useNotificationTasks";

// Events
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Default light polling interval for cross-session unread freshness (D12
 * fallback until a Medplum WebSocket subscription is available).
 */
const DEFAULT_POLL_INTERVAL_MS = 60_000;
const SERVICE_APPROVAL_CODE_TOKEN = `${NOTIFICATION_TASK_TYPE_SYSTEM}|${NOTIFICATION_TYPE_DEFINITIONS.SERVICE_APPROVAL.code}`;
const SERVICE_APPROVAL_PAGE_SIZE = 1_000;

/**
 * Options for {@link useNotificationTaskCount}.
 * @agentic connections
 */
export type UseNotificationTaskCountOptions = {
  /** Current user's PractitionerRole ids; unread count is scoped to these owners. */
  ownerRoleIds: string[];
  enabled?: boolean;
  pollIntervalMs?: number;
};

/**
 * Unread clinical-notification count for the current user (server-side
 * `business-status = unread`). Refreshes on notification domain events and via
 * light polling.
 * @agentic connections
 */
export function useNotificationTaskCount({
  ownerRoleIds,
  enabled = true,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: UseNotificationTaskCountOptions): {
  count: number;
  loading: boolean;
  refetch: () => void;
} {
  // Read the client through a ref so the fetch effect never re-fires on client
  // identity churn (the Medplum client is a stable singleton at runtime).
  const medplum = useMedplum();
  const medplumRef = useRef(medplum);
  useEffect(() => {
    medplumRef.current = medplum;
  }, [medplum]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const ownerParam = ownerRoleIds
    .map((id) => `PractitionerRole/${id}`)
    .join(",");
  const active = enabled && ownerRoleIds.length > 0;

  useEffect(() => {
    if (!active) {
      startTransition(() => setCount(0));
      return;
    }

    let cancelled = false;
    startTransition(() => setLoading(true));

    const run = async (): Promise<number> => {
      const client = medplumRef.current;
      const [allUnreadResult, serviceApprovalsResult] =
        await Promise.allSettled([
          client.search("Task", {
            ...buildUnreadNotificationTaskSearchParams(),
            owner: ownerParam,
            _summary: "count",
            _count: "0",
            _total: "accurate",
          }),
          client.search("Task", {
            ...buildUnreadNotificationTaskSearchParams(),
            code: SERVICE_APPROVAL_CODE_TOKEN,
            owner: ownerParam,
            _count: String(SERVICE_APPROVAL_PAGE_SIZE),
            _elements: "id,focus,code,owner,status",
            _total: "accurate",
          }),
        ]);

      if (allUnreadResult.status === "rejected") throw allUnreadResult.reason;
      const allUnread = allUnreadResult.value;
      const total = allUnread.total ?? 0;
      if (serviceApprovalsResult.status === "rejected") return total;
      const serviceApprovals = serviceApprovalsResult.value;
      const serviceApprovalTasks =
        serviceApprovals.entry?.flatMap(({ resource }) =>
          resource ? [resource] : [],
        ) ?? [];
      const serviceApprovalTotal =
        serviceApprovals.total ?? serviceApprovalTasks.length;
      if (serviceApprovalTotal === 0) return total;

      let liveServiceApprovalCount = 0;
      try {
        liveServiceApprovalCount = (
          await reconcileServiceApprovalNotices(
            client,
            serviceApprovalTasks,
            new Set(ownerParam.split(",").filter(Boolean)),
          )
        ).length;
      } catch {
        // Match the drawer's PHI-safe behavior: unverifiable service-approval
        // notices do not contribute to the visible count.
      }

      // ponytail: Medplum caps one search page at 1,000. If unread approvals
      // approach that ceiling, replace this with paginated retrieval.
      return Math.max(
        0,
        total - serviceApprovalTotal + liveServiceApprovalCount,
      );
    };

    run()
      .then((nextCount) => {
        if (!cancelled) startTransition(() => setCount(nextCount));
      })
      .catch(() => {
        // Count failures must not break the shell; show zero.
        if (!cancelled) startTransition(() => setCount(0));
      })
      .finally(() => {
        if (!cancelled) startTransition(() => setLoading(false));
      });

    return () => {
      cancelled = true;
    };
  }, [ownerParam, active, reloadKey]);

  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEventDrivenRefetch(
    [
      DomainEventNames.NotificationTaskCreated,
      DomainEventNames.NotificationTaskUpdated,
    ],
    { refetch: triggerReload, enabled: active },
  );

  useEffect(() => {
    if (!active) return;
    const id = setInterval(triggerReload, pollIntervalMs);
    return () => clearInterval(id);
  }, [active, pollIntervalMs, triggerReload]);

  return { count, loading, refetch: triggerReload };
}
